import { ICellRendererParams, IGetRowsParams } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import { FC, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import DeleteConfirmationModal from '../../common/DeleteConfirmationModal/DeleteConfirmationModal';
import Header from '../../common/Header/Header';
import {
  ButtonLabel,
  Buttons, ButtonsSeparator,
  Page,
  TableWrapper,
} from '../../common/TableWithButtonsRightLayout/TableWithButtonsRightLayout.styles';
import { Message } from '../../common/TwoButtonModal/TwoButtonModal.styles';
import Button from '../../components/Button/Button';
import { Variant } from '../../components/Button/Button.types';
import { Options } from '../../components/Dropdown/Dropdown.types';
import { Loading } from '../../components/Loading/Loading';
import Modal from '../../components/Modal/Modal';
import Table from '../../components/Table/Table';
import { showToast } from '../../components/Toast/Toast';
import { ToastTypes } from '../../components/Toast/Toast.types';
import { useUser } from '../../services/auth/auth.service';
import { Security } from '../../services/auth/auth.types';
import {
  getExtracts,
  useExtractWebStoreXref,
  useDeleteExtractErrors,
  useExtractMasterFile,
} from '../../services/extract/extract.service';
import { useSusGuiImageExtract } from '../../services/image/image.service';
import { useManInvGroups } from '../../services/manInv/manInv.service';
import { ManInvGroup } from '../../services/manInv/manInv.types';
import {
  genericErrorShowToastOptions,
  useGenericErrorMessage,
} from '../../utils/useGenericErrorMessage/useGenericErrorMessage';
import { LoadingWrapper, ModalButtons } from './Extract.styles';
import { FetchStatus, ModalStatus } from './Extract.types';
import ManInvExtractModal from './ManInvExtractModal/ManInvExtractModal';

const Extract: FC = () => {
  const gridRef = useRef<AgGridReact>(null);
  const navigate = useNavigate();
  const { data: userData } = useUser(true);
  const user = userData?.data;
  const [totalRecords, setTotalRecords] = useState<number | undefined>(undefined);
  const [manInvModalStatus, setManInvModalStatus] = useState<ModalStatus>(ModalStatus.Closed);
  const [manInvFetchStatus, setManInvFetchStatus] = useState<FetchStatus>(FetchStatus.Default);
  const [manInvGroupOptions, setManInvGroupOptions] = useState<Options[]>([]);
  const [modalMasterFile, setModalMasterFile] = useState(false);
  const [modalWebStoreXref, setModalWebStoreXref] = useState(false);
  const [modalGuiImage, setModalGuiImage] = useState(false);

  const [deleteConfirmationModalVisible, setDeleteConfirmationModalVisible] = useState(false);

  const closeDeleteConfirmationModal = () => {
    setDeleteConfirmationModalVisible(false);
  };

  const handleRemoveClick = () => {
    setDeleteConfirmationModalVisible(true);
  };

  const { mutate: deleteExtractErrors, isLoading: isDeleteLoading } = useDeleteExtractErrors();

  const handleDeleteExtractErrors = () => {
    deleteExtractErrors(undefined, {
      onSuccess: () => {
        closeDeleteConfirmationModal();
        showToast({
          id: 'deleteExtractErrorsSuccess',
          label: 'User Specific Extract Errors have been successfully deleted.',
          toastType: ToastTypes.Success,
        });
      },
    });
  };

  /*
	when an extract is successful, toggling this state (true/false) causes the data source to be updated because toggleDataSource is a dependency in the useMemo for extractDataSource. the data source is updated to the same data source since nothing inside that useMemo actually changes (this is ag-grid's recommended way of triggering a refetch when you know data has changed)
	*/
  const [toggleDataSource, setToggleDataSource] = useState(false);
  // const {
  //   mutate: extractDscRequest,
  //   isError: dscIsError,
  //   isLoading: isDscExtractLoading,
  //   error: dscError,
  // } = useExtractDsc();

  // TODO: Reinstate mutate when all stores utilize MOA (AI-1841)
  const {
    mutate: extractMasterFileRequest,
    isError: masterFileIsError,
    isLoading: isMasterFileExtractLoading,
    error: masterFileError,
  } = useExtractMasterFile();
  const {
    mutate: extractWebStoreXrefRequest,
    isError: webStoreXrefIsError,
    isLoading: isWebStoreXrefExtractLoading,
    error: webStoreXrefError,
  } = useExtractWebStoreXref();
  const { data, error, isError } = useManInvGroups(manInvFetchStatus === FetchStatus.Loading);

  const {
    mutate: extractGuiImageRequest,
    isError: extractGuiImageIsError,
    isLoading: extractGuiImageIsLoading,
    error: extractGuiImageError,
  } = useSusGuiImageExtract();

  const refetch = () => {
    gridRef.current?.api.refreshInfiniteCache();
  };

  useEffect(() => {
    const interval = window.setInterval(refetch, 18000);
    return () => {
      window.clearInterval(interval);
    };
  }, []);

  // close the modal and show an error toast if error fetching or extracting
  useEffect(() => {
    if (error || isError) {
      showToast({ ...genericErrorShowToastOptions, label: 'Error loading mandatory inventory groups.' });
      setManInvFetchStatus(FetchStatus.Default);
    }
  }, [error, isError]);

  useGenericErrorMessage([
    // { isError: dscIsError, error: dscError },
    { isError: webStoreXrefIsError, error: webStoreXrefError },
    //TODO: Reinstate isError state when all stores utilize MOA (AI-1841)
    { isError: masterFileIsError, error: masterFileError },
    { isError: extractGuiImageIsError, error: extractGuiImageError },
  ]);

  // set dropdown options once they have been fetched
  useEffect(() => {
    if (data?.data && manInvFetchStatus !== FetchStatus.Default) {
      if (data.data.length === 0) {
        // if fetch was successful but no manInv groups exist
        showToast({
          label: 'There are no mandatory inventory groups to extract.',
          id: 'noManInvGroupsToExtractError',
          toastType: ToastTypes.Error,
        });
        // reset fetch status
        setManInvFetchStatus(FetchStatus.Default);
      } else {
        setManInvFetchStatus(FetchStatus.Success);
        if (manInvModalStatus === ModalStatus.Opening) setManInvModalStatus(ModalStatus.Open);
        setManInvGroupOptions(
          data.data.map((manInvGroup: ManInvGroup) => {
            return { value: manInvGroup.id.toString(), label: manInvGroup.description };
          })
        );
      }
    }
  }, [data]);

  // const extractDsc = () => {
  //   extractDscRequest(undefined, {
  //     onSuccess: () => {
  //       setToggleDataSource(!toggleDataSource);
  //       showToast({
  //         id: 'extractDscSuccess',
  //         label: 'DSC Extract Request was successfully submitted',
  //       });
  //     },
  //   });
  // };

  // TODO: Reinstate extractMasterFile when all stores utilize MOA (AI-1841)
  const extractMasterFile = () => {
    extractMasterFileRequest(undefined, {
      onSuccess: () => {
        setToggleDataSource(!toggleDataSource);
        setModalMasterFile(false);
        showToast({
          id: 'extractMasterFileSuccess',
          label: 'Master File Extract Request was successfully submitted',
        });
      },
    });
  };

  const extractWebStoreXref = () => {
    extractWebStoreXrefRequest(undefined, {
      onSuccess: () => {
        setModalWebStoreXref(false);
        showToast({
          id: 'extractWebStoreXrefSuccess',
          label: 'WebStoreXref Extract Request was successfully submitted',
        });
      },
    });
  };

  const extractCouponGroup = () => {
    navigate('/extract/coupongroup');
  };

  const extractMenu = () => {
    navigate('/extract/menu');
  };

  const activateMenu = () => {
    navigate('/extract/menu/activate');
  };

  const extractGui = () => {
    extractGuiImageRequest(undefined, {
      onSuccess: () => {
        setModalGuiImage(false);
        showToast({
          id: 'extractGuiImageSuccess',
          label: 'SUS GUI images extract submitted successfully.',
        });
      },
    });
  };

  const showManInvModal = () => {
    if (data?.data && data?.data.length > 0) {
      setManInvModalStatus(ModalStatus.Open);
      setManInvFetchStatus(FetchStatus.Loading);
    } else {
      setManInvFetchStatus(FetchStatus.Loading);
      setManInvModalStatus(ModalStatus.Opening);
    }
  };

  const hideManInvModal = () => {
    setManInvModalStatus(ModalStatus.Closed);
  };

  const extractsDataSource = useMemo(() => {
    return {
      totalRecords,
      getRows: async (params: IGetRowsParams) => {
        const response = await getExtracts({
          startRow: params.startRow,
          endRow: params.endRow,
          sortModel: params.sortModel
            .map((sortModelItem) => {
              return { ...sortModelItem, sortDir: sortModelItem.sort };
            })
            .concat([{ sortDir: 'desc', sort: 'desc', colId: 'createdDate' }]), // default sort order (by createdDate) is added after any user sort orders
          filterModel: params.filterModel,
        });
        if (Object.keys(params.filterModel).length === 0) setTotalRecords(response.data.totalRecords);
        // Note- when using the "infinite row model", you will need to use the gridApi to manually show and hide the "no rows overlay".
        if (response.data.totalRecords === 0) {
          gridRef.current?.api?.showNoRowsOverlay();
        } else {
          gridRef.current?.api?.hideOverlay();
        }
        params.successCallback(response.data.contents, response.data.totalRecords);
      },
    };
  }, [toggleDataSource]);

  const LoadingCellRenderer = (props: ICellRendererParams) => {
    if (props.data?.userDisplayName) {
      return props.data.userDisplayName;
    } else if (props.data?.id) {
      return <></>;
    } else {
      return (
        <LoadingWrapper>
          <Loading size={12} />
        </LoadingWrapper>
      );
    }
  };

  return (
    <>
      <Header breadcrumb={{ to: '/tables', label: 'All tables' }} title='Extract Queue' />
      <Page>
        <TableWrapper>
          <Table
            ref={gridRef}
            rowModelType='infinite'
            datasource={extractsDataSource}
            returnedRecords={totalRecords}
            cacheBlockSize={100}
            height={'100%'}
            columnDefs={[
              {
                headerName: 'Name',
                field: 'userDisplayName',
                width: 65,
                maxWidth: 180,
                flex: 1,
                sortable: true,
                cellRenderer: LoadingCellRenderer,
                tooltipValueGetter: (params) => params.value,
              },
              {
                headerName: 'Type',
                field: 'extractType',
                filter: 'agTextColumnFilter',
                floatingFilter: true,
                flex: 1,
                // minWidth: 50,
                width: 65,
                maxWidth: 110,
                sortable: true,
              },
              {
                headerName: 'Status',
                field: 'extractStatus',
                filter: 'agTextColumnFilter',
                floatingFilter: true,
                flex: 1,
                // minWidth: 50,
                maxWidth: 65,
                width: 50,
                sortable: true,
                valueFormatter: (params) =>
                  params.value != null && ['L', 'O', 'G'].includes(params.value.toUpperCase()) ? 'O' : params.value,
              },
              {
                headerName: 'Request Date',
                field: 'createdDate',
                filter: 'agTextColumnFilter',
                floatingFilter: true,
                flex: 1,
                maxWidth: 110,
                width: 100,
                sortable: true,
                valueFormatter: (params) => (params.value ? new Date(params.value).toLocaleDateString() : ''),
                tooltipValueGetter: (params) =>
                  params.value
                    ? new Date(params.value).toLocaleDateString() + ' ' + new Date(params.value).toLocaleTimeString()
                    : '',
              },
              {
                headerName: 'Details',
                field: 'details',
                filter: 'agTextColumnFilter',
                floatingFilter: true,
                flex: 2,
                minWidth: 180,
                sortable: true,
                resizable: false,
              },
              {
                headerName: 'Reason',
                field: 'reason',
                filter: 'agTextColumnFilter',
                floatingFilter: true,
                flex: 4,
                minWidth: 180,
                sortable: true,
                tooltipValueGetter: (params) => params.value,
              },
            ]}
            footerButtons={[
              {
                label: 'Remove',
                variant: Variant.Secondary,
                onClick: handleRemoveClick,
              },
            ]}
          />
        </TableWrapper>
        <Buttons>
          <ButtonLabel>Extract Actions</ButtonLabel>
          {user?.security === Security.Admin ? (
            //TODO: Reinstate Button when all stores utilize MOA (AI-1841)
            <Button
              label='Master File'
              testId='EXtractMasterFileActionButton'
              variant={Variant.Secondary}
              onClick={() => setModalMasterFile(true)}
              isLoading={isMasterFileExtractLoading}
              loadingLabel={'Extracting'}
            />
          ) : (
            <></>
          )}
          <Button
            label='Web Store Xref'
            testId='ExtractWebStoreXrefActionButton'
            variant={Variant.Secondary}
            onClick={() => setModalWebStoreXref(true)}
            isLoading={isWebStoreXrefExtractLoading}
            loadingLabel={'Extracting'}
          />
          <Button
            label='GUI Extract'
            testId='GuiExtractActionButton'
            variant={Variant.Secondary}
            onClick={() => setModalGuiImage(true)}
            isLoading={extractGuiImageIsLoading}
          />
          <ButtonsSeparator />
          {/* <Button
            label='Delivery Charge'
            variant={Variant.Secondary}
            onClick={extractDsc}
            isLoading={isDscExtractLoading}
            loadingLabel={'Extracting'}
          /> */}

          <Button
            label='Mandatory Inv.'
            testId='ExtractMandatoryInvActionButton'
            variant={Variant.Secondary}
            onClick={showManInvModal}
          />
          <Button
            label='Extract Menu'
            testId='ExtractMenuActionButton'
            variant={Variant.Secondary}
            onClick={extractMenu}
          />
          <Button
            label='Activate Menu'
            testId='ActivateMenuActionButton'
            variant={Variant.Secondary}
            onClick={activateMenu}
          />
          <Button
            label='Coupon Group'
            testId='ExtractCouponActionButton'
            variant={Variant.Secondary}
            onClick={extractCouponGroup}
          />
        </Buttons>
      </Page>
      <ManInvExtractModal
        open={manInvModalStatus === ModalStatus.Open}
        onClose={hideManInvModal}
        options={manInvGroupOptions}
        setToggleDataSource={setToggleDataSource}
        toggleDataSource={toggleDataSource}
      />
      <DeleteConfirmationModal
        open={deleteConfirmationModalVisible}
        onClose={closeDeleteConfirmationModal}
        onDelete={handleDeleteExtractErrors}
        cancelBtnTestId='CancelDeleteExtractModalButton'
        confirmBtnTestId='ConfirmDeleteExtractModalButton'
        isLoading={isDeleteLoading}
      />
      <Modal
        id='masterFileExtract'
        open={modalMasterFile}
        title={'Are you sure you want to trigger the Master File extraction?'}
        onClose={() => setModalMasterFile(false)}
      >
        <div>
          <Message></Message>
          <ModalButtons>
            <Button variant={Variant.Secondary} label='Cancel' onClick={() => setModalMasterFile(false)} />
            <Button
              variant={Variant.Primary}
              label='Extract'
              onClick={extractMasterFile}
              isLoading={isMasterFileExtractLoading}
              loadingLabel='Extracting...'
              testId={'ConfirmXrefModalButton'}
            />
          </ModalButtons>
        </div>
      </Modal>
      <Modal
        id='guiImageExtract'
        open={modalGuiImage}
        title={'Are you sure you want to trigger GUI Images extraction?'}
        onClose={() => setModalGuiImage(false)}
      >
        <div>
          <Message></Message>
          <ModalButtons>
            <Button variant={Variant.Secondary} label='Cancel' onClick={() => setModalGuiImage(false)} />
            <Button
              variant={Variant.Primary}
              label='Extract'
              onClick={extractGui}
              isLoading={extractGuiImageIsLoading}
              loadingLabel='Extracting...'
            />
          </ModalButtons>
        </div>
      </Modal>
      <Modal
        id='webStoreXrefExtract'
        open={modalWebStoreXref}
        title={'Are you sure you want to trigger the Web Store Xref extraction?'}
        onClose={() => setModalWebStoreXref(false)}
      >
        <div>
          <Message></Message>
          <ModalButtons>
            <Button variant={Variant.Secondary} label='Cancel' onClick={() => setModalWebStoreXref(false)} />
            <Button
              variant={Variant.Primary}
              label='Extract'
              onClick={extractWebStoreXref}
              isLoading={isWebStoreXrefExtractLoading}
              loadingLabel='Extracting...'
              testId={'ConfirmXrefModalButton'}
            />
          </ModalButtons>
        </div>
      </Modal>
    </>
  );
};

export default Extract;
